import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CrmService } from '../../../../../services/crm.service';
import { BaseService } from '../../../../../services/base.service';
import { NotificationService } from '../../../../../services/notifications.service';
import { AttributesForm, UpdateForm } from './create-update-olx-advert.interface';
import { DialogService } from '../../../../../services/dialog.service';
import { OlxAdvertService } from '../../../../../services/olx-advert.service';
import {
  CHANNEL_TYPE,
  CURRENCY,
  DEFAULT_ERROR,
  ERROR_MESSAGES,
  activeTranslations,
  MODAL_TYPES,
  UPLOAD_FILE_TYPES,
} from '../../../../../enum/constants';
import { ModalService } from '../../../../../services/modal.service';
import { PageEvent } from '@angular/material/paginator';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { IError } from '../../../../../interfaces';
import { PlannerComponent } from '../../../../../modals/planner/planner.component';
import { FileUploadNewComponent } from '../../../../../modals/file-upload-new/file-upload-new.component';
import moment from 'moment';
import { OLX_PRICE_TYPES } from '../../../../price-editor/price-editor.interface';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-create-update-olx-advert',
  templateUrl: './create-update-olx-advert.component.html',
  styleUrl: './create-update-olx-advert.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CreateUpdateOlxAdvertComponent implements OnInit, OnDestroy {
  private readonly destroy$: Subject<null> = new Subject<null>();

  public updateForm!: FormGroup<UpdateForm>;
  public attributeForm!: FormGroup<AttributesForm>;
  public isFormSubmitted = false;
  public priceTypeControl: FormControl = new FormControl('', [Validators.required]);
  public isLoader: boolean = false;
  public isDistrict: boolean = false;
  public isChangeCity: boolean = false;
  public currentIndex = 0;
  public current_category_id: any = 0;
  public advert: any = {};
  public dataAttributes: any = [];
  public channel_id!: string;
  public images: { url: string }[] = [];
  public categories: any[] = [];
  public cities: any[] = [];
  public cityPage: number = 1;
  public districtPage: number = 1;
  public cityTotal: number = 0;
  public districtTotal: number = 0;
  private citySubscription: Subscription;
  private districtSubscription: Subscription;
  public districts: any[] = [];
  public current_city: any;
  public current_district: any;
  public blocks: any;
  public searchedCity: string = '';
  public type_action!: string;
  public photos_limit: number = 0;
  public calendar: any;
  public planned_at: any;
  public is_planner: boolean = false;
  public isDraft: any;
  public current_category: string = '';
  public choosed_city: any = {};

  @Input() payload: any;
  @Output() close = new EventEmitter<any>();

  constructor(
    private readonly translate: TranslateService,
    public crmService: CrmService,
    public baseService: BaseService,
    public dialogService: DialogService,
    public notificationService: NotificationService,
    public olxAdvertService: OlxAdvertService,
    public readonly modalService: ModalService,
  ) {
    this.citySubscription = Subscription.EMPTY;
    this.districtSubscription = Subscription.EMPTY;
  }

  public ngOnInit(): void {
    const options = this.payload ?? {};
    this.advert = options.advert;
    this.channel_id = options.channel_id;
    this.type_action = options.type_action;
    this.is_planner = options.is_planner;
    this.images = this.advert?.images;

    if (this.advert) {
      this.getCategoryAttributes();
      this.isLoader = true;
      this.getCategoryById(this.advert.category_id);
      const price = this.advert.price;
      if (price?.value) {
        this.priceTypeControl.setValue('price');
      } else if (price?.value === 0) {
        this.priceTypeControl.setValue('free');
      } else if (price?.trade) {
        this.priceTypeControl.setValue('trade');
      }
      this.getCategory();
      this.getCategoryAdvert();
      this.getCity();
      this.initForm();
    } else {
      this.images = [];
      this.getCategoryAdvert();
      this.getCity();
      this.initForm();
    }
  }

  getCategoryById(category_id: number) {
    this.olxAdvertService.getCategoryById(category_id).subscribe((res) => {
      if (res) {
        this.current_category = res.name;
      }
    });
  }

  getCategory() {
    this.olxAdvertService.getCategoryPhoto(this.advert.category_id).subscribe((res) => {
      if (res) {
        this.photos_limit = res.photos_limit;
        this.setEmptyBlocks();
      }
    });
  }

  getCategoryAdvert() {
    this.olxAdvertService.getCategories(this.current_category_id).subscribe({
      next: (res) => {
        if (res) this.categories.push(res);
      },
    });
  }

  selectCategory(category: any, i: any) {
    if (i !== this.categories.length - 1) {
      this.categories.splice(i + 1);
      this.current_category_id = category.category_id;
    }

    this.current_category_id = category.category_id;

    if (!category.is_leaf) {
      this.updateForm.controls.category_id.setValue(null);
      this.getCategoryAdvert();
    } else {
      this.olxAdvertService
        .getCategoryPhoto(this.current_category_id)
        .pipe(takeUntil(this.destroy$))
        .subscribe((res) => {
          if (res) {
            this.photos_limit = res.photos_limit;
            this.setEmptyBlocks();
            this.getCategoryAttributesAtCreate();
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  public initForm() {
    this.getCity();

    const attributes: any = {};
    if (this.dataAttributes) {
      this.dataAttributes.forEach((att: any) => {
        const currentValue: any = this.advert.attributes.find((v: any) => v.code === att.code);
        const validates: any[] = [];
        if (att.validation.required) validates.push(Validators.required);
        if (att.validation.min !== 0) validates.push(Validators.min(att.validation.min));
        if (att.validation.max) validates.push(Validators.max(att.validation.max));
        if (att.values.length && att.validation.allow_multiple_values) {
          attributes[att.code] = new FormControl(currentValue?.values || [], validates);
        } else {
          attributes[att.code] = new FormControl(currentValue?.value || currentValue?.values, validates);
        }
      });
    }
    this.updateForm = new FormGroup<UpdateForm>({
      title: new FormControl(this.advert?.title ?? null, [
        Validators.minLength(16),
        Validators.maxLength(70),
        Validators.required,
      ]),
      description: new FormControl(this.advert?.description ?? null, [
        Validators.minLength(40),
        Validators.maxLength(9000),
        Validators.required,
      ]),
      category_id: new FormControl(this.advert ? this.advert.category_id : null, [Validators.required]),
      price: new FormGroup({
        value: new FormControl(this.advert?.price.value ?? null),
        currency: new FormControl(this.advert?.price.currency ?? 'UAH'),
        trade: new FormControl(this.advert?.price.trade ?? null),
        negotiable: new FormControl(this.advert?.price.negotiable ?? null),
      }),
      advertiser_type: new FormControl(this.advert?.type ?? 'private'),
      contact: new FormGroup({
        name: new FormControl(this.advert ? this.advert.contact?.name : null),
        phone: new FormControl(this.advert ? this.advert.contact?.phone : null),
      }),
      location: new FormGroup({
        city_id: new FormControl(this.advert ? (this.advert.location.city_id ?? null) : (this.current_city ?? null), [
          Validators.required,
        ]),
        district_id: new FormControl(
          this.advert ? (this.advert.location.district_id ?? null) : (this.current_district ?? null),
        ),
        latitude: new FormControl(null),
        longitude: new FormControl(null),
      }),
    });

    if (this.advert) {
      this.attributeForm = new FormGroup<AttributesForm>({
        ...attributes,
      });
    }
  }

  initializeAttributes(): void {
    const attributes: any = {};
    if (this.dataAttributes) {
      this.dataAttributes.forEach((att: any) => {
        const validates: any[] = [];
        if (att.validation.required) validates.push(Validators.required);
        if (att.validation.min !== 0) validates.push(Validators.min(att.validation.min));
        if (att.validation.max) validates.push(Validators.max(att.validation.max));
        if (att.values.length && att.validation.allow_multiple_values) {
          attributes[att.code] = new FormControl([], validates);
        } else {
          attributes[att.code] = new FormControl(null, validates);
        }
      });
    }

    this.attributeForm = new FormGroup<AttributesForm>({ ...attributes });
    // const attributesGroup = this.attributeForm.get('attributes') as FormGroup<AttributesForm>;
    // this.dataAttributes.forEach((attribute: any) => {
    // attributesGroup.addControl(attribute.code, new FormControl(null));
    // });
    this.isLoader = false;
  }

  public onCloseSidebar(): void {
    this.close.emit('close');
  }

  protected save(publishNow: boolean = false) {
    this.isFormSubmitted = true;
    if (this.updateForm.valid && this.attributeForm.valid) {
      const formValue = this.updateForm.getRawValue();

      switch (this.priceTypeControl.value) {
        case 'free':
          formValue.price.value = 0;
          formValue.price.trade = false;
          formValue.price.negotiable = false;
          break;
        case 'trade':
          formValue.price.value = null;
          formValue.price.trade = true;
          formValue.price.negotiable = false;
          break;
        case 'price':
          formValue.price.trade = false;
          break;
      }

      const attributeValues = this.attributeForm.getRawValue();
      const attributesArray = Object.keys(attributeValues).map((key) => {
        const attribute = attributeValues[key];
        const data: any = {
          code: key,
        };

        if (Array.isArray(attribute)) {
          data.values = attribute;
        } else {
          data.value = attribute;
        }

        return data;
      });

      if (publishNow) {
        this.current_category_id = this.advert.category_id;
        this.createAdvert(formValue, attributesArray);
        return;
      }

      if (this.type_action === 'CREATE') {
        if (this.planned_at) this.createAdvertPlanner(formValue, attributesArray);
        else this.createAdvert(formValue, attributesArray);
        return;
      }

      if (this.type_action === 'UPDATE') {
        if (this.planned_at) this.updatePlannerAdvert(formValue, attributesArray);
        else this.updateAdvert(formValue, attributesArray);
        return;
      }
    } else {
      this.updateForm.markAllAsTouched();
      this.attributeForm.markAllAsTouched();
      this.priceTypeControl.markAsTouched();
    }
  }

  createAdvert(formValue: any, attributesArray: any) {
    const payloadCreate = {
      images: this.images,
      ...formValue,
      location: {
        city_id: formValue.location.city_id,
        district_id: formValue.location.district_id?.district_id,
        longitude: formValue.location.city_id.longitude,
        latitude: formValue.location.city_id.latitude,
      },
      attributes: attributesArray,
      category_id: this.current_category_id,
    };
    this.olxAdvertService
      .createAdvert(this.channel_id, payloadCreate)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          if (this.advert?.planned_at) {
            const payload = {
              id: this.advert?.id,
              status: 'CREATED',
            };
            this.olxAdvertService.updateStatusPacket(payload).subscribe();
          }
          this.onCloseSidebar();
          this.notificationService.showSuccess(
            '',
            activeTranslations.notifications_texts.success.successfully_created,
          );
        },
        error: (res) => {
          (res.error?.errors || []).forEach((err: IError) => {
            if (this.advert?.planned_at) {
              const payload = {
                id: this.advert?.id,
                status: 'CANCELED',
              };
              this.olxAdvertService.updateStatusPacket(payload).subscribe();
            }

            if (err.code === 'TOO_HIGH_ADVERT_PRICE') {
              this.updateForm.controls.price.controls.value.setErrors({ too_high_advert_price: true });
              return;
            }

            if (err.code === 'ADVERT_CANNOT_SPECIFIED_CURRENCY') {
              this.updateForm.controls.price.controls.currency.setErrors({ advert_cannot_specified_currency: true });
              return;
            }

            this.notificationService.showError(
              activeTranslations.notifications_texts.error.error,
              ERROR_MESSAGES[err.code] || DEFAULT_ERROR,
            );
          });
        },
      });
  }

  updateAdvert(formValue: any, attributesArray: any) {
    const payloadUpdate = {
      images: this.images,
      ...formValue,
      location: {
        city_id: formValue.location.city_id,
        district_id: formValue.location.district_id?.district_id,
        longitude: formValue.location.city_id.longitude,
        latitude: formValue.location.city_id.latitude,
      },
      attributes: attributesArray,
    };
    this.olxAdvertService
      .updateAdvert(this.channel_id, this.advert.id, payloadUpdate)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.onCloseSidebar();
          this.notificationService.showSuccess(
            '',
            activeTranslations.notifications_texts.success.successfully_updated,
          );
        },
        error: (res) => {
          (res.error?.errors || []).forEach((err: IError) => {
            if (err.code === 'TOO_HIGH_ADVERT_PRICE') {
              this.updateForm.controls.price.controls.value.setErrors({ too_high_advert_price: true });
              return;
            }
            if (err.code === 'ADVERT_CANNOT_SPECIFIED_CURRENCY') {
              this.updateForm.controls.price.controls.currency.setErrors({ advert_cannot_specified_currency: true });
              return;
            }

            this.notificationService.showError(
              activeTranslations.notifications_texts.error.error,
              ERROR_MESSAGES[err.code] || DEFAULT_ERROR,
            );
          });
        },
      });
  }

  updatePlannerAdvert(formValue: any, attributesArray: any) {
    const event = {
      id: this.advert.id,
    };
    const advert = {
      images: this.images,
      ...formValue,
      city: {
        ...this.choosed_city,
        city_id: formValue.location.city_id,
        district_id: formValue.location.district_id?.district_id,
        longitude: formValue.location.city_id.longitude,
        latitude: formValue.location.city_id.latitude,
      },
      location: {
        city_id: formValue.location.city_id,
        district_id: formValue.location.district_id?.district_id,
        longitude: formValue.location.city_id.longitude,
        latitude: formValue.location.city_id.latitude,
      },
      attributes: attributesArray,
    };
    const payload = {
      x_data: {
        channel_id: this.channel_id,
        advert: advert,
      },
    };

    this.olxAdvertService
      .updatePlanner2(payload, event)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.onCloseSidebar();
        },
        error: () => {
          this.onCloseSidebar();
        },
      });
  }

  createAdvertPlanner(formValue: any, attributesArray: any) {
    const advert = {
      images: this.images,
      ...formValue,
      attributes: attributesArray,
      category_id: this.current_category_id,
      city: {
        ...this.choosed_city,
        city_id: formValue.location.city_id,
        district_id: formValue.location.district_id?.district_id,
        longitude: formValue.location.city_id.longitude,
        latitude: formValue.location.city_id.latitude,
      },
      location: {
        city_id: formValue.location.city_id,
        district_id: formValue.location.district_id?.district_id,
        longitude: formValue.location.city_id.longitude,
        latitude: formValue.location.city_id.latitude,
      },
    };
    const payloadPlanner = {
      type: 'CREATE_ADVERT',
      planned_at: this.planned_at,
      name: this.calendar.name,
      status: this.isDraft,
      x_data: {
        channel_id: this.channel_id,
        advert: advert,
      },
    };
    this.olxAdvertService
      .createPlanner(payloadPlanner)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.onCloseSidebar();
        },
        error: () => {
          this.onCloseSidebar();
        },
      });
  }

  cancel() {
    this.onCloseSidebar();
  }

  getCategoryAttributes() {
    this.olxAdvertService.getCategoryAttributes(this.advert.category_id, this.channel_id).subscribe((res) => {
      this.dataAttributes = res;
      this.isLoader = false;
      this.initForm();
    });
  }

  getCategoryAttributesAtCreate() {
    this.olxAdvertService.getCategoryAttributes(this.current_category_id, this.channel_id).subscribe((res) => {
      this.dataAttributes = res;
      this.initializeAttributes();
    });
  }

  checkItem(attributeCode: string, valueCode: string) {
    const currentValues = this.attributeForm.controls[attributeCode].value || [];

    if (currentValues.includes(valueCode)) {
      this.attributeForm.controls[attributeCode].setValue(currentValues.filter((code: string) => code !== valueCode));
    } else {
      this.attributeForm.controls[attributeCode].setValue([...currentValues, valueCode]);
    }
  }

  setCurrentIndex(event: any) {
    this.currentIndex = parseInt(event, 10) || 0;
  }

  onPageChange(event: PageEvent): void {
    this.currentIndex = event.pageIndex;
    this.setCurrentIndex(this.currentIndex);
  }

  setEmptyBlocks() {
    if (this.images) {
      const emptyCount = this.photos_limit - this.images.length;
      this.blocks = new Array(emptyCount > 0 ? emptyCount : 0);
    } else {
      this.blocks = new Array(this.photos_limit);
    }
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.images, event.previousIndex, event.currentIndex);
  }

  deletePhoto(index: any) {
    this.images.splice(index, 1);
    this.setEmptyBlocks();
  }

  getDistrict(city_id: string, searchTerm: string = '') {
    const payload = {
      city_id: city_id,
      page: this.districtPage,
      take: 25,
      search: searchTerm,
    };
    if (this.districtSubscription) {
      this.districtSubscription.unsubscribe();
    }
    this.districtSubscription = this.olxAdvertService.getDistrics(payload).subscribe({
      next: (res: any) => {
        if (res.items.length) {
          this.districts = [...this.districts, ...res.items];
          this.districtTotal = res.total;
          this.isDistrict = true;
          this.setValidatorsDistrict();
        }
        // if (this.currentContact && !this.contacts.some(obj => _.isEqual(obj, this.currentContact)))
        //   this.contacts.unshift(this.currentContact);
      },
      error: (e: any) => {
        (e.error?.errors || []).forEach((err: IError) => {
          this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
          this.isDistrict = false;
          this.setValidatorsDistrict();
        });
      },
    });
  }

  getCity(searchTerm: string = '') {
    const payload = {
      page: this.cityPage,
      take: 25,
      search: searchTerm,
    };
    if (this.citySubscription) {
      this.citySubscription.unsubscribe();
    }
    this.citySubscription = this.olxAdvertService.getCity(payload).subscribe({
      next: (res: any) => {
        const items = [...res.items].map((v) => {
          const fullname = [];
          if (v.name) fullname.push(v.name);
          if (v.municipality) fullname.push(v.municipality);
          return {
            ...v,
            fullname: fullname.join(', '),
          };
        });
        this.cities = [...this.cities, ...items];
        this.cityTotal = res.total;
        // if (this.currentContact && !this.contacts.some(obj => _.isEqual(obj, this.currentContact)))
        //   this.contacts.unshift(this.currentContact);
      },
      error: (e: any) => {
        (e.error?.errors || []).forEach((err: IError) => {
          this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
        });
      },
    });
  }

  scrollToEndCity() {
    if (this.cities.length !== this.cityTotal) {
      ++this.cityPage;
      this.getCity(this.searchedCity);
    }
  }

  searchCity(e: any) {
    this.resetCities();
    this.searchedCity = e.term;
    this.getCity(this.searchedCity);
  }

  searchDistrict(e: any) {
    this.resetDistricts();
    this.searchedCity = e.term;
    this.getDistrict(this.current_city, this.searchedCity);
  }

  private resetCities() {
    this.cityPage = 1;
    this.cityTotal = 0;
    this.cities = [];
    this.resetDistricts();
  }

  private resetDistricts() {
    this.districtPage = 1;
    this.districtTotal = 0;
    this.districts = [];
  }

  selectDistrict(e: any) {
    this.current_district = e.district_id;
    this.updateForm.controls.location.controls.latitude.setValue(e.latitude);
    this.updateForm.controls.location.controls.longitude.setValue(e.longitude);
  }

  selectCity(e: any) {
    if (this.type_action === 'UPDATE' && !this.isChangeCity) {
      this.getDistrict(e);
    } else {
      this.choosed_city = {
        name: e.name,
        municipality: e.municipality,
      };
      this.updateForm.controls.location.controls.district_id.reset();
      this.current_city = e.city_id;
      this.isDistrict = false;
      this.getDistrict(e.city_id);
    }
    this.setValidatorsDistrict();
  }

  scrollToEndDistrict() {
    if (this.districts.length !== this.districtTotal) {
      ++this.districtPage;
      this.getDistrict(this.current_city, this.searchedCity);
    }
  }

  setValidatorsDistrict() {
    if (this.isDistrict) {
      this.updateForm.controls.location.controls.district_id.setValidators(Validators.required);
    } else {
      this.updateForm.controls.location.controls.district_id.clearValidators();
    }
    this.updateForm.controls.location.controls.district_id.updateValueAndValidity();
  }

  openPlanner() {
    this.modalService
      .openModal({
        title: 'planner_create',
        translate: true,
        component: PlannerComponent,
        data: {
          modalType: MODAL_TYPES.CREATE_ADVERT,
        },
      })
      .subscribe((res) => {
        if (res) {
          const dateToString = moment(res.x_data.date).format('DD/MM/YYYY');
          const dateTime = moment(`${dateToString} ${res.x_data.time}`, 'DD/MM/YYYY HH:mm');
          this.calendar = res;
          this.planned_at = dateTime;
          this.isDraft = res.x_data.status;
        }
      });
  }

  public cancelPlanner() {
    this.planned_at = null;
  }

  setChoice(event: any) {
    if (event.value === 'price') {
      this.updateForm.controls.price.get('value')?.setValidators(Validators.required);
      this.updateForm.controls.price.get('currency')?.setValidators(Validators.required);
    } else {
      const priceControl = this.updateForm.controls.price.get('value');
      priceControl?.setValue(priceControl?.value ?? 0);
      this.updateForm.controls.price.get('value')?.clearValidators();
      this.updateForm.controls.price.get('currency')?.clearValidators();
    }
  }

  protected openFileUpload() {
    if (this.images.length >= this.photos_limit) {
      this.notificationService.showWarning(
        this.translate.instant('notifications_texts.warn.warn'),
        this.translate.instant('notifications_texts.warn.no_more_upload_files', { limit: this.photos_limit }),
      );
      return;
    } else {
      const allowedMediaTypes = UPLOAD_FILE_TYPES[CHANNEL_TYPE.OLX]?.allowed_media_types;

      this.modalService
        .openModal({
          translate: true,
          component: FileUploadNewComponent,
          title: 'upload_file',
          height: 'auto',
          data: {
            length: this.images.length,
            max_upload_files: this.photos_limit,
            channel_type: CHANNEL_TYPE.OLX,
            allowed_media_types: allowedMediaTypes,
          },
        })
        .subscribe((res) => {
          if (res) {
            if (!this.images) {
              this.images = [];
            }
            res.forEach((img: any) => {
              this.images.push({ url: img.link });
            });
            this.blocks = this.blocks.slice(res.length);
            this.setEmptyBlocks();
          }
        });
    }
  }

  protected readonly CURRENCY = CURRENCY;
  protected readonly OLX_PRICE_TYPES = OLX_PRICE_TYPES;
}
